import { useState, useEffect } from "react";
import { message } from "antd";
import { departmentPerformanceService } from "Services/AdminServices/ReportServices/DepartmentPerformanceService";

function useDepartmentPerformance() {
  const [performanceData, setPerformanceData] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchingPerformance, setFetchingPerformance] = useState(false);
  const [error, setError] = useState(null);

  // Fetch departments list on component mount
  useEffect(() => {
    const fetchDepartmentsList = async () => {
      try {
        setLoading(true);
        setError(null);
        const departmentsList = await departmentPerformanceService.getDepartmentList();
        
        // Handle different response structures
        let departmentArray = [];
        if (Array.isArray(departmentsList)) {
          departmentArray = departmentsList;
        } else if (departmentsList && departmentsList.data && Array.isArray(departmentsList.data)) {
          departmentArray = departmentsList.data;
        } else {
          throw new Error("Unexpected API response format for departments list");
        }
        
        setDepartments(departmentArray);
        
        // Select the first department by default if available
        if (departmentArray.length > 0) {
          // Try to find the department ID field (could be id, departmentId, etc.)
          const firstDept = departmentArray[0];
          const deptId = firstDept.id || firstDept.departmentId || firstDept.value;
          if (deptId) {
            setSelectedDepartmentId(deptId);
          }
        }
      } catch (err) {
        console.error("Failed to fetch departments:", err);
        setError("Failed to load departments. Please try again.");
        message.error("Failed to load departments");
      } finally {
        setLoading(false);
      }
    };

    fetchDepartmentsList();
  }, []);

  // Fetch performance data when a department is selected
  useEffect(() => {
    if (selectedDepartmentId) {
      const fetchPerformanceData = async () => {
        try {
          setFetchingPerformance(true);
          setError(null);
          const data = await departmentPerformanceService.getDepartmentPerformance(selectedDepartmentId);
          setPerformanceData(data);
        } catch (err) {
          console.error("Failed to fetch department performance:", err);
          setError("Failed to load performance data. Please try again.");
          message.error("Failed to load performance data");
          setPerformanceData(null);
        } finally {
          setFetchingPerformance(false);
        }
      };

      fetchPerformanceData();
    }
  }, [selectedDepartmentId]);

  const handleDepartmentChange = (departmentId) => {
    setSelectedDepartmentId(departmentId);
  };

  return {
    performanceData,
    departments,
    selectedDepartmentId,
    loading,
    fetchingPerformance,
    error,
    handleDepartmentChange,
  };
}

export default useDepartmentPerformance;